/**
 * Admin utility functions for centralized admin user management
 * This helps avoid hardcoded email checks throughout the codebase
 */

const publicAdminEmailRaw = process.env.NEXT_PUBLIC_ADMIN_EMAIL?.trim() || null;
const serverAdminEmailRaw = process.env.ADMIN_EMAIL?.trim() || null;

// Parse comma-separated list of admin emails
const parseAdminEmails = (emailString: string | null): string[] => {
  if (!emailString) return [];
  return emailString
    .split(',')
    .map(email => email.trim().toLowerCase())
    .filter(email => email.length > 0);
};

const publicAdminEmails = parseAdminEmails(publicAdminEmailRaw);
const serverAdminEmails = parseAdminEmails(serverAdminEmailRaw);

// Combine server and public admin emails (server takes precedence)
const allAdminEmails = [...new Set([...serverAdminEmails, ...publicAdminEmails])];

// Legacy single email export (uses first admin email for backwards compatibility)
export const ADMIN_EMAIL = allAdminEmails[0] || null;

// Label for UI – only uses publicly exposed env to avoid hydration mismatches.
export const ADMIN_EMAIL_LABEL = publicAdminEmailRaw;

if (typeof window === 'undefined' && allAdminEmails.length === 0) {
  console.warn('⚠️ ADMIN_EMAIL is not set. Admin features will be disabled.');
}

/**
 * Check if a user is an admin by email
 * Supports multiple admin emails via comma-separated ADMIN_EMAIL env variable
 */
export function isAdminByEmail(email: string): boolean {
  if (!email) {
    return false;
  }

  const normalized = email.toLowerCase().trim();
  return allAdminEmails.includes(normalized);
}

/**
 * Check if a user object has admin privileges
 * Checks email and role for admin validation
 */
export function isAdminUser(user: { email: string; role?: string }): boolean {
  return isAdminByEmail(user.email) || user.role === 'admin';
}

/**
 * Check if a user is a super admin (the primary admin email)
 * Super admin is the first email in ADMIN_EMAIL env variable
 */
export function isSuperAdmin(email: string): boolean {
  if (!email || !ADMIN_EMAIL) {
    return false;
  }
  const normalized = email.toLowerCase().trim();
  return normalized === ADMIN_EMAIL.toLowerCase().trim();
}

/**
 * Get admin user configuration
 * Centralized place for admin-specific settings
 */
export function getAdminConfig() {
  return {
    email: ADMIN_EMAIL,
    plan: 'admin',
    credits: 999999999,
    role: 'admin'
  };
}
